<template>
  <div class="transfer-details fade-in">
    <!-- Header -->
    <ProductDetailsHeader
        title="Stock Transfer"
        icon="box-arrow-right"
        :main="transfer"
        :header-details="headerDetails"
    >
      <template #header-actions>
        <div class="d-flex flex-wrap align-items-center gap-2">
          <select
              v-if="transfer.status !== 'complete' && transferProducts.data.length > 0"
              class="form-select w-auto"
              v-model="transferStatus"
              @change="confirmDialog(getConfirmTitle, updateTransferStatus, 'Are you sure you want to change transfer status to ' + transferStatus)" data="">
            <option value="" disabled selected>Change Status</option>
            <option v-if="transfer.status === 'pending'" value="complete" class="text-success fs-5">Complete Transfer</option>
          </select>
        </div>
      </template>
    </ProductDetailsHeader>

    <!-- Store Products -->
    <StoreProducts
        v-if="transfer.status === 'pending'"
        :per-page="5"
        :marked-products="transferProducts"
        @row-click="populateAddProduct"
    >
      <template #header-actions>
        <button
            v-if="profile.role !== 'user'"
            class="btn btn-success"
            @click="confirmDialog('Transfer All Products', transferAll, 'Are you sure you want to transfer all products')">
          <i class="bi bi-box-arrow-right"></i> Transfer All Products
        </button>
      </template>
    </StoreProducts>

    <!-- Transfer Products -->
    <ProductDetailsPages
        title="Transfer"
        :loading="loading"
        :detail-products="transferProducts"
        :current-page="detailsPage"
        :current-per-page="detailsPerPage"
        :current-search="detailsSearch"
        @update:page="onDetailsPageChange"
        @update:per-page="onDetailsPerPageChange"
        @update:search="onDetailsSearchChange"
        :main="transfer"
        @edit-product="populateTransferEdit"
        @delete-product="deleteTransferProduct"
    >
      <template #header-actions>
        <button
            v-if="profile.role !== 'user' && transferProducts.data.length > 0 && transfer.status === 'pending'"
            class="btn btn-outline-danger"
            @click="confirmDialog('Delete All Transfer Products', transferDeleteAll, 'Are you sure you want to delete all transfer products')">
          <i class="bi bi-trash me-1"></i> Delete All Products
        </button>
      </template>
      <template #footer-actions>
        <button
            v-if="profile.role !== 'user' && transferProducts.data.length > 0 && transfer.status === 'pending'"
            class="btn btn-outline-danger"
            @click="confirmDialog('Delete All Transfer Products', transferDeleteAll, 'Are you sure you want to delete all transfer products')">
          <i class="bi bi-trash me-1"></i> Delete All Products
        </button>
        <select
            v-if="transfer.status !== 'complete' && transferProducts.data.length > 0"
            class="form-select w-auto"
            v-model="transferStatus"
            @change="confirmDialog(getConfirmTitle, updateTransferStatus, 'Are you sure you want to change transfer status to ' + transferStatus)" data="">
          <option value="" disabled selected>Change Status</option>
          <option v-if="transfer.status === 'pending'" value="complete" class="text-success fs-5">Complete Transfer</option>
        </select>
      </template>
    </ProductDetailsPages>

    <!-- Add/Edit Product Modal -->
    <Modal :open="addProductModal" @closed="addProductModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0" v-if="addProductMode === 'add'">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Product to Transfer
        </h5>
        <h5 class="modal-title m-0" v-else>
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Transfer Product
        </h5>
        <button @click="addProductModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-6">
              <label for="name" class="form-label fw-medium mb-2">Product Name:</label>
              <input type="text" class="form-control" :value="selectedProduct.name" disabled>
            </div>
            <div class="form-group col-md-6">
              <label for="quantity" class="form-label fw-medium mb-2">Quantity:</label>
              <div class="input-group">
                <input type="number" class="form-control" autofocus="autofocus" v-model="transferDetails.quantity" min="1" required>
              </div>
            </div>
            <div class="form-group col-md-6 mt-3">
              <label for="sell" class="form-label fw-medium mb-2">Selling Price:</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input type="number" class="form-control" v-model="transferDetails.sell" min="0" step="0.01" required>
              </div>
            </div>
            <div class="form-group col-md-6 mt-3">
              <label class="form-label fw-medium mb-2">Total Value:</label>
              <div class="alert alert-info py-2 mb-0">
                <i class="bi bi-info-circle me-2"></i>
                {{ formatNumber(transferDetails.quantity * transferDetails.sell) }}
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="addProductModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" v-if="addProductMode === 'add'" @click="addTransferProduct">
          <i class="bi bi-plus-lg me-1"></i> Add Product
        </button>
        <button type="button" class="btn btn-primary" v-else @click="updateTransferProduct(transferDetails.id)">
          <i class="bi bi-check-lg me-1"></i> Update Product
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import transferService from "@/api/transferService";
import confirmDialog  from "@/utils/confirmDialog";
import notify from "@/utils/notify";
import ProductDetailsHeader from "@/components/details/ProductDetailsHeader.vue";
import ProductDetailsPages from "@/components/details/ProductDetailsPages.vue";
import StoreProducts from "@/components/store/StoreProducts.vue";

export default {
  name: "TransferDetails",

  components: { ProductDetailsHeader, ProductDetailsPages, StoreProducts },

  data(){
    return {
      profile: {},
      transferStatus: '',
      transfer: {
        store: {},
        user: {},
      },
      transferDetails: {
        transfer_id: this.$route.params.id,
        product_id: '',
        quantity: '',
        sell: ''
      },
      selectedProduct: {},
      transferProducts: { data: [] },
      detailsPage: 1,
      detailsPerPage: 20,
      detailsSearch: '',
      loading: true,
      addProductModal: false,
      addProductMode: 'add',
    }
  },

  methods:{
    confirmDialog,
    formatNumber,
    async updateTransferStatus() {
      await transferService.approveTransfer(this.$route.params.id)
      this.transfer.status = this.transferStatus
    },
    async getTransferDetails() {
      this.transferProducts = await transferService.getTransferDetails(this.$route.params.id, {
        page: this.detailsPage,
        per_page: this.detailsPerPage
      })
      this.loading = false
    },
    onDetailsPageChange(page){
      this.detailsPage = page;
      this.getTransferDetails();
    },
    onDetailsPerPageChange(perPage){
      this.detailsPerPage = perPage;
      this.detailsPage = 1;
      this.getTransferDetails();
    },
    onDetailsSearchChange(search){
      this.detailsSearch = search;
      this.detailsPage = 1;
      this.getTransferDetails();
    },
    async transferAll() {
      this.loading = true
      let data = {
        transfer_id: this.$route.params.id
      }
      await transferService.transferAll(data)
      this.loading = false
    },
    async transferDeleteAll() {
      this.loading = true
      let data = {
        transfer_id: this.$route.params.id
      }
      await transferService.transferDeleAll(data)
      this.loading = false
    },
    populateAddProduct(storeProduct) {
      this.addProductModal = true;
      this.addProductMode = 'add';
      this.selectedProduct.name = storeProduct.product.name;
      this.selectedProduct.stock = storeProduct.stock;
      this.transferDetails.product_id = storeProduct.product_id;
      this.transferDetails.quantity = storeProduct.stock;
      this.transferDetails.sell = storeProduct.price;
    },
    async addTransferProduct() {
      if (this.transferDetails.quantity > (this.selectedProduct.stock || 0)) {
        notify.alert.error('Insufficient stock quantity', 3000);
        return;
      }
      let response = await transferService.createTransferDetails(this.transferDetails);
      if (response) {
        this.addProductModal = false;
        await this.getTransferDetails();
      }
    },
    async updateTransferProduct() {
      let response = await transferService.updateTransferDetails(this.transferDetails, this.transferDetails.id);
      if (response) {
        this.addProductModal = false;
        await this.getTransferDetails();
      }
    },
    populateTransferEdit(transfer) {
      this.selectedProduct.name = transfer.product.name;
      this.selectedProduct.stock = transfer.product.stock;
      this.transferDetails.product_id = transfer.product_id;
      this.transferDetails.id = transfer.id;
      this.transferDetails.quantity = transfer.quantity;
      this.transferDetails.sell = transfer.sell;
      this.addProductModal = true;
      this.addProductMode = 'edit';
    },
    async deleteTransferProduct(id) {
      let response = await transferService.deleteTransferDetails(id)
      if (response) {
        await this.getTransferDetails()
      }
    },
  },

  computed: {
    headerDetails() {
      return [
        { icon: 'hash', label: 'Store', key: 'store.name' },
        { icon: 'person', label: 'By', key: 'user.first_name' },
      ];
    },
    getConfirmTitle() {
      switch (this.transferStatus) {
        case 'complete':
          return 'Complete Transfer'
        default:
          return 'Update Transfer Status'
      }
    }
  },

  created: async function () {
    this.transfer = await transferService.getTransfer(this.$route.params.id)
    await this.getTransferDetails()
  },

  watch: {
    addProductModal: function () {
      if (!this.addProductModal) {
        this.selectedProduct = []
        this.transferDetails = {
          transfer_id: this.$route.params.id
        }
      }
    }
  },

}
</script>

<style scoped>
</style>
